/** 后端两层规则引擎（取料层 + 提炼层）的响应结构，与 XunLiaoTs/src/rules/types.ts 对齐 */

export type Grade = 'L0' | 'L1' | 'L2' | 'L3' | 'L4'

export type CiteType = 'official_page' | 'datasheet_pdf' | 'self_fetched' | 'search_snippet' | 'llm_annotation'

export type FieldStatus =
  | 'provided_verified'
  | 'provided_unverified'
  | 'provided_conflict'
  | 'evidence_proven'
  | 'model_only'
  | 'suspected'
  | 'missing'
  | 'undetermined'

export const STATUS_LABEL: Record<FieldStatus, string> = {
  provided_verified: '已提供·已核实',
  provided_unverified: '已提供·未核实',
  provided_conflict: '已提供·有冲突',
  evidence_proven: '料已证',
  model_only: '仅模型称',
  suspected: '疑似',
  missing: '缺失',
  undetermined: '无法判定',
}

export const GRADE_LABEL: Record<Grade, string> = {
  L0: 'L0 官网/样本',
  L1: 'L1 授权/国标',
  L2: 'L2 认证 B2B',
  L3: 'L3 转载/论坛',
  L4: 'L4 内容农场',
}

export interface EvidenceSpan {
  start: number
  end: number
  kind: 'model' | 'param' | 'table_row'
  text: string
}

export interface Candidate {
  value: string
  sourceId: string
  span: EvidenceSpan
  grade: Grade
  modelAnchored: boolean
  normalized: string
  via: 'pattern' | 'model_decode' | 'llm_extract'
  /** 只靠单位类型/位置归属命中，未与参数名共现 → 最高只判疑似 */
  weak: boolean
  gates: { boundary: boolean; normalized: boolean; cooccur: boolean; anchored: boolean }
  passes: boolean
}

export interface FieldResult {
  name: string
  tier: 'must' | 'important'
  when?: string
  userValue?: string
  userGiven: boolean
  bestValue?: string
  normalizedValue?: string
  status: FieldStatus
  candidates: Candidate[]
  evidence: {
    literalHit: boolean
    normalizedHit: boolean
    cooccur: boolean
    modelAnchored: boolean
    independentCount: number
    topGrade: Grade | null
  }
  conflict?: { userValue: string; recommendedValue: string; reason: string }
  action?: string
}

export interface ChecklistItem {
  level: 'block' | 'suggest' | 'tip'
  field?: string
  message: string
  action: string
}

export interface ConflictRecord {
  field: string
  userValue?: string
  recommendedValue: string
  sides: { value: string; sourceId: string; grade: Grade }[]
  reason: string
}

export interface AnchorValue {
  value: string
  start: number
  via: string
  confidence: 'high' | 'medium' | 'low'
}

export interface Anchors {
  brand?: AnchorValue
  model?: AnchorValue
  name?: AnchorValue
  specs: AnchorValue[]
  integrityFlags: string[]
}

export interface Sufficiency {
  level: 'sufficient' | 'insufficient' | 'empty'
  mustCoverage: [number, number]
  hasTopGrade: boolean
  modelAnchored: boolean
  missingMust: string[]
  triedQueries: string[]
}

export interface QuerySpec {
  q: string
  type: 'exact' | 'guess' | 'targeted'
  reason: string
  round: number
}

export interface SourceSummary {
  id: string
  title: string
  url: string
  siteName: string
  grade: Grade
  citeType: CiteType
  independentGroup: string
  isRepostOf?: string
  modelAnchored: boolean
  matchedQueries: string[]
}

export interface DistillResult {
  category: { name: string | null; confident: boolean; via: 'rule' | 'profile' | 'source_hint' | 'llm'; alternatives: string[] }
  fields: FieldResult[]
  conflicts: ConflictRecord[]
  otherModelValues: { field: string; value: string; sourceId: string; reason: string }[]
  checklist: { block: ChecklistItem[]; suggest: ChecklistItem[]; tip: ChecklistItem[] }
  standardDescription: { text: string; spans: { text: string; sourceId: string; span: EvidenceSpan }[] }
  fetchSufficiency: Sufficiency
  audit: { rulesVersion: string; elapsedMs: number; evidenceSource: string }
}

export interface MaterialQueryResponse {
  materialText: string
  anchors: Anchors
  sufficiency: Sufficiency
  queries: QuerySpec[]
  sources: SourceSummary[]
  result: DistillResult
}
